import { modalStore } from '@/stores/modal.store';
import MakeDeposit from '@/components/Modals/MakeDeposit/MakeDeposit';
import LoaderModal from '@/components/LoaderModal/LoaderModal';
import ViewCoinflip from '@/components/Modals/ViewCoinflip/ViewCoinflip';
import JackpotDeposit from '@/components/Modals/JackpotDeposit/JackpotDeposit';
import JoinCoinflip from '@/components/Modals/JoinCoinflip/JoinCoinflip';
import CreateCoinflip from '@/components/Modals/CreateCoinflip/CreateCoinflip';
import CartModal from '@/components/Modals/CartModal/CartModal';
import NewTicket from '@/components/Modals/NewTicket/NewTicket';
import FairplayModal from '@/components/Modals/Fairplay/FairplayModal';
import Notification from '@/components/Modals/Notification/Notification';
import SteamTrade from '@/components/Modals/SteamTrade/SteamTrade';
import CryptoDeposit from '@/components/Modals/Crypto/CryptoDeposit';
import CryptoAddress from '@/components/Modals/Crypto/CryptoAddress';
import JackpotSteamDeposit from '@/components/Modals/JackpotSteamDeposit/JackpotSteamDeposit';
import CreateSteamCoinflip from '@/components/Modals/CreateSteamCoinflip/CreateSteamCoinflip';
import JoinSteamCoinflip from '@/components/Modals/JoinSteamCoinflip/JoinSteamCoinflip';
import AddCase from '@/components/Modals/AddCase/AddCase';
import CaseModal from '@/components/Modals/Case/CaseModal';
import { useEffect, useRef } from 'react';
import { useOnClickOutside } from '@/utils/hooks/use-on-click-outside';
import JoinBattle from '@/components/Modals/JoinBattle/JoinBattle';
import BattleFairness from '@/components/Modals/BattleFairness/BattleFairness';
import CryptoWithdraw from '@/components/Modals/Crypto/CryptoWithdraw';
import WithdrawSuccess from '@/components/Modals/WithdrawSuccess/WithdrawSuccess';
import AdminAddBet from '@/components/Modals/AdminAddBet/AdminAddBet';
import AdminAddCoinflip from '@/components/Modals/AdminAddBet/AdminAddCoinflip';
import AdminJoinCoinflip from '@/components/Modals/AdminAddBet/AdminJoinCoinflip';
import ProfileModal from '@/components/Profile/ProfileModal';

interface Props {}

// Modals can hint at their preferred max-width via data.modalSize
const SIZE_CLASS: Record<string, string> = {
    sm: 'w-[760px]',
    md: 'w-[980px]',
    lg: 'w-[1320px]',
};

const ModalWrapper = ({}: Props) => {
    const { data, modal, component, title, toggleModal, setData } = modalStore((state) => state);
    const content = useRef<HTMLDivElement>(null);

    function close() {
        if (data.onClose) {
            data.onClose();
        } else {
            toggleModal();
            setData({});
        }
    }

    useEffect(() => {
        if (!modal) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') close();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modal]);

    // Lock page scroll while the modal is open so a wheel over the overlay (or
    // scroll-chaining once an inner list hits its end) can't scroll the page
    // behind it. Compensate the scrollbar width to avoid a layout shift.
    useEffect(() => {
        if (!modal) return;
        const { body } = document;
        const prevOverflow = body.style.overflow;
        const prevPaddingRight = body.style.paddingRight;
        const scrollbarW = window.innerWidth - document.documentElement.clientWidth;
        body.style.overflow = 'hidden';
        if (scrollbarW > 0) body.style.paddingRight = `${scrollbarW}px`;
        return () => {
            body.style.overflow = prevOverflow;
            body.style.paddingRight = prevPaddingRight;
        };
    }, [modal]);

    function renderComponent() {
        switch (component) {
            case 'Jackpot site deposit':
                return <JackpotDeposit minbet={data.minbet} />;
            case 'Jackpot steam deposit':
                return <JackpotSteamDeposit minbet={data.minbet} />;
            case 'Make deposit':
                return <MakeDeposit />;
            case 'Loader':
                return <LoaderModal />;
            case 'View coinflip':
                return <ViewCoinflip id={data.id} game={data.game} />;
            case 'Join coinflip':
                return <JoinCoinflip id={data.id} />;
            case 'Create coinflip':
                return <CreateCoinflip />;
            case 'New ticket':
                return <NewTicket load={data.load} onCreated={data.onCreated} />;
            case 'Fairplay':
                return (
                    <FairplayModal
                        user={data.user}
                        id={data.id}
                        secret={data.secret}
                        hash={data.hash}
                    />
                );
            case 'Cart':
                return (
                    <CartModal
                        items={data.items}
                        load={data.load}
                        setPage={data.setPage}
                        selectedGame={data.selectedGame}
                        type={data.type}
                    />
                );
            case 'Notification':
                return <Notification text={data.text} type={data.type} />;
            case 'Crypto deposit':
                return <CryptoDeposit />;
            case 'Crypto withdraw':
                return <CryptoWithdraw />;
            case 'Withdraw success':
                return <WithdrawSuccess />;
            case 'Crypto address':
                return (
                    <CryptoAddress
                        id={data.id}
                        wallet={data.wallet}
                        value={data.value}
                        currency={data.currency}
                        status={data.status}
                        back={data.back}
                    />
                );
            case 'Steam trade':
                return <SteamTrade tradeId={data.tradeId} coinflipGameId={data.coinflipGameId} />;
            case 'Add case':
                return <AddCase />;
            case 'Case':
                return <CaseModal items={data.items} />;
            case 'Create steam coinflip':
                return <CreateSteamCoinflip />;
            case 'Join steam coinflip':
                return <JoinSteamCoinflip id={data.id} />;
            case 'Join battle':
                return <JoinBattle id={data.id} />;
            case 'Battle fairness':
                return <BattleFairness game={data.game} />;
            case 'Admin add bet':
                return <AdminAddBet />;
            case 'Admin add coinflip':
                return <AdminAddCoinflip />;
            case 'Admin join coinflip':
                return <AdminJoinCoinflip />;
            case 'Profile':
                return <ProfileModal />;
        }
    }

    useOnClickOutside(content, () => close());

    if (!modal) return null;

    // Default to md for coinflip view, lg for inventory pickers (coinflip + jackpot
    // join/create flows + market cart), sm otherwise.
    const size: string =
        data.modalSize ||
        (component === 'View coinflip' || component === 'Case'
            ? 'md'
            : component === 'Join coinflip' ||
                component === 'Create coinflip' ||
                component === 'Join steam coinflip' ||
                component === 'Create steam coinflip' ||
                component === 'Jackpot site deposit' ||
                component === 'Jackpot steam deposit' ||
                component === 'Add case' ||
                component === 'Cart' ||
                component === 'Admin add bet' ||
                component === 'Admin add coinflip' ||
                component === 'Admin join coinflip' ||
                component === 'Profile'
              ? 'lg'
              : 'auto');
    const sizeCls = SIZE_CLASS[size] ?? '';

    return (
        <div
            className="fixed inset-0 z-[200] grid place-items-center p-6 max-md:p-2"
            style={{
                // No backdrop-filter: Chrome re-rasterizes a backdrop blur on
                // every descendant repaint (scroll/hover), which flashes the
                // background and leaves hairline stripes along the edges. A
                // denser opaque base obscures the page instead.
                background:
                    'radial-gradient(900px 560px at 50% 38%, rgba(139,109,255,0.18), transparent 70%), radial-gradient(700px 500px at 80% 100%, rgba(58,214,255,0.10), transparent 65%), rgba(6,5,14,0.92)',
            }}
        >
            <div
                ref={content}
                className={`relative flex flex-col rounded-fl-lg px-10 pt-16 pb-9 max-md:px-4 max-md:pt-14 max-md:pb-5 max-h-[calc(100vh-48px)] max-w-[calc(100vw-48px)] max-md:max-h-[calc(100vh-16px)] max-md:max-w-[calc(100vw-16px)] ${sizeCls} max-md:w-full`}
                style={{
                    // Near-opaque (no backdrop blur) — see overlay note above.
                    background:
                        'linear-gradient(180deg, rgba(30,24,58,0.985), rgba(16,12,34,0.99))',
                    border: '1px solid color-mix(in srgb, #8b6dff 32%, transparent)',
                    boxShadow:
                        '0 36px 90px -24px rgba(0,0,0,0.75), 0 0 60px -18px rgba(139,109,255,0.45), inset 0 1px 0 rgba(255,255,255,0.07)',
                    animation: 'modalIn 0.32s cubic-bezier(0.34, 1.56, 0.64, 1)',
                }}
            >
                {/* On desktop the title overlaps the top edge (-top-5).
                    On mobile the modal hugs the viewport, so anything sitting
                    above the shell gets clipped — keep the title INSIDE the
                    shell on phones instead. */}
                <div
                    className="absolute -top-5 max-md:top-3 left-1/2 -translate-x-1/2 px-9 max-md:px-4 py-3 max-md:py-1.5 rounded-fl text-[13px] max-md:text-[11px] font-bold tracking-[0.16em] uppercase text-white whitespace-nowrap max-w-[calc(100vw-32px)] overflow-hidden text-ellipsis"
                    style={{
                        background:
                            'linear-gradient(180deg, rgba(30,24,58,0.97), rgba(20,17,40,0.97))',
                        border: '1px solid color-mix(in srgb, #8b6dff 45%, transparent)',
                        boxShadow:
                            '0 0 20px -4px rgba(139,109,255,0.7), 0 10px 24px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)',
                        textShadow: '0 0 12px rgba(139,109,255,0.5)',
                    }}
                >
                    {title}
                </div>
                <button
                    onClick={close}
                    aria-label="Close"
                    className="absolute top-3.5 right-3.5 w-9 h-9 grid place-items-center rounded-[28%] border border-stroke bg-white/[0.04] text-text_2 hover:rotate-90 hover:text-white hover:border-[#ff6262]/60 hover:bg-[rgba(255,98,98,0.12)] hover:shadow-[0_0_18px_-4px_rgba(255,98,98,0.7)] transition-all"
                >
                    <svg
                        viewBox="0 0 24 24"
                        className="w-3.5 h-3.5 stroke-current fill-none [stroke-width:2.2] [stroke-linecap:round]"
                    >
                        <path d="M6 6l12 12M18 6l-12 12" />
                    </svg>
                </button>
                <div className="flex flex-col flex-1 min-h-0 overflow-auto overscroll-contain no-scrollbar">
                    {renderComponent()}
                </div>
            </div>
        </div>
    );
};

export default ModalWrapper;
